import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { FileText, Home } from 'lucide-react'
import { LEGAL, LEGAL_ENTITY_DISPLAY } from '@/lib/legal'

/**
 * Public terms of service. Identifies the operating business; linked from the
 * footer and signup. Kept consistent with the Privacy and SMS Consent pages.
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-muted/30 p-4 py-12">
      <div className="max-w-2xl mx-auto bg-card text-card-foreground rounded-lg border shadow-sm p-8">
        <FileText className="h-12 w-12 text-primary" />
        <h1 className="mt-6 text-3xl font-bold">Terms of Service</h1>
        <p className="mt-2 text-muted-foreground">
          Effective {LEGAL.effectiveDate}. {LEGAL_ENTITY_DISPLAY}
        </p>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Agreement</h2>
          <p className="text-muted-foreground">
            These Terms govern your use of {LEGAL.product}, operated by {LEGAL.entity} ("we", "us").
            By using {LEGAL.product} you agree to these Terms. If you do not agree, do not use the
            service.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">The service</h2>
          <p className="text-muted-foreground">
            {LEGAL.product} lets an organizer create a "drop" and invite specific people they know to
            upload photos to it. You are responsible for your account and for activity under it.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Your responsibilities</h2>
          <ul className="list-disc pl-5 text-muted-foreground space-y-1">
            <li>Only invite people you personally know and who have agreed to be contacted. When you
              send an SMS or email invitation, you confirm you have that person's permission.</li>
            <li>Only upload content you have the right to share, and nothing unlawful, infringing, or
              harmful.</li>
            <li>Do not misuse the service, attempt to breach its security, or use it to send
              unsolicited, marketing, or bulk messages.</li>
          </ul>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Messaging</h2>
          <p className="text-muted-foreground">
            {LEGAL.product} sends transactional invitation and reminder texts on an organizer's
            behalf, one-to-one, only after the organizer confirms the recipient's consent. Recipients
            can reply <strong>STOP</strong> to opt out at any time. See the{' '}
            <Link className="text-primary underline" to="/sms-consent">SMS Messaging Policy</Link>.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Plans &amp; payment</h2>
          <p className="text-muted-foreground">
            Paid plans are billed through our payment processor per the pricing shown at checkout.
            Fees are non-refundable except where required by law.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Content &amp; ownership</h2>
          <p className="text-muted-foreground">
            You retain rights to the content you upload. You grant us the limited rights needed to
            host, process, and display that content to operate the service.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Disclaimers &amp; liability</h2>
          <p className="text-muted-foreground">
            The service is provided "as is" without warranties of any kind. To the maximum extent
            permitted by law, {LEGAL.entity} is not liable for indirect, incidental, or consequential
            damages, and our total liability is limited to the amount you paid us in the twelve months
            before the claim.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Termination &amp; changes</h2>
          <p className="text-muted-foreground">
            We may suspend or terminate accounts that violate these Terms. We may update these Terms;
            material changes will be reflected by the effective date above.
          </p>
        </section>

        <section className="mt-8 space-y-3">
          <h2 className="text-xl font-semibold">Governing law &amp; contact</h2>
          <p className="text-muted-foreground">
            These Terms are governed by the laws of the State of {LEGAL.governingState}. Questions?
            Email{' '}
            <a className="text-primary underline" href={`mailto:${LEGAL.supportEmail}`}>
              {LEGAL.supportEmail}
            </a>
            {LEGAL.address ? `, or write to us at ${LEGAL.address}` : ''}.
          </p>
        </section>

        <div className="mt-10 flex gap-3">
          <Button asChild variant="outline">
            <Link to="/privacy">Privacy Policy</Link>
          </Button>
          <Button asChild>
            <Link to="/">
              <Home className="mr-2 h-4 w-4" />
              Back to {LEGAL.product}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
